import React from 'react';

import {
  QuestionCircleIcon, DropletIcon, MinusCircleIcon, CheckIcon,
} from '@vlabs/icons';
import cn from 'classnames';

import type { Option } from '@vlabs/monokit/shared/ui/options-filter';

import st from './ColorGridFilter.module.sass';

const WITH_ICON_VALUES = ['undefined', 'other', 'null'];

interface ColorGridFilterProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: Option[];
  label?: string | undefined;
}

export function ColorGridFilter({
  value,
  onChange,
  options,
  label = undefined,
}: ColorGridFilterProps): React.JSX.Element {
  const toggle = (optionValue: string) => {
    const next = value.includes(optionValue)
      ? value.filter((color) => color !== optionValue)
      : value.concat(optionValue);

    onChange?.(next);
  };

  const selectedLabels = options
    .filter((option) => value.includes(option.value))
    .map((option) => option.label.toLowerCase())
    .join(', ');

  return (
    <React.Fragment>
      <div>
        {!!label && (
          <label className="Subtitle-2">
            {label}
            {!!selectedLabels && ': '}
          </label>
        )}
        {!!selectedLabels && <span>{selectedLabels}</span>}
      </div>

      <div className={st.ColorsGrid}>
        {options.map((option) => {
          const isActive = value.includes(option.value);
          const withIcon = WITH_ICON_VALUES.includes(option.value);

          return (
            <div
              aria-label={option.label}
              className={cn(
                st.ColorIcon,
                st[`ColorIcon_${option.value}` as keyof typeof st],
                isActive && withIcon && st.ColorIcon_activeBorder,
              )}
              key={option.value}
              onClick={() => toggle(option.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggle(option.value);
                }
              }}
              role="button"
              tabIndex={0}
              title={option.label}
            >
              {option.value === 'undefined' && <QuestionCircleIcon className={st.InnerIcon} />}
              {option.value === 'other' && <DropletIcon className={st.InnerIcon} />}
              {option.value === 'null' && <MinusCircleIcon className={st.InnerIcon} />}
              {isActive && !withIcon ? <CheckIcon className={st.InnerIcon} /> : null}
            </div>
          );
        })}
      </div>
    </React.Fragment>
  );
}
